import store from '@/store';
import { establishGontijoSession, signOutGontijoSession } from '@/actions/gontijoSession';
import { selectLoginOnCheckoutEnabled } from 'selectors/gontijoSessionSelectors';

const UNAUTHORIZED = 401;

/**
 * Whether a tokenless announcement means the session is over.
 *
 * The widget publishes `{ user: null }` for two different things. A sign-out, or a stored token the
 * backend turned down (a 401 on the widget's own revalidation), ends the session. A failed sign-in
 * attempt — a mistyped password, a wrong code — says nothing about the session already open, and
 * signing out on it would log the buyer out of every tab from a stale form in one of them.
 * @param {*} error - The `error` the widget published alongside the null user.
 * @returns {boolean} True when the funnel should sign out.
 */
const endsSession = (error) => !error || error.response?.status === UNAUTHORIZED;

/**
 * Turns the widget's announcement into the funnel's session.
 *
 * The widget owns the form and the code step; everything after the token exists stays here — where
 * it is stored, when it expires, whether it is still good, and which payload it rides on. That is
 * why `expires` travels with the token: the widget keeps a cookie with a lifetime in days, which
 * cannot express a session the ERP ends at a given minute.
 *
 * Guarded on the flag as well as on the payload. The widget only mounts for Gontijo today, but an
 * event bus is open to anyone, and a session must never be established for a brand that does not
 * have checkout login.
 * @param {object} [event] - What the widget published. A missing payload reads as a sign-out.
 * @param {object|null} event.user - `{ token, expires, name, email }`, or null when the buyer signed out.
 *   `email` is kept with the session: it is the address the passengers form offers later.
 * @param {*} [event.error] - Set when the null user comes from a failure rather than a sign-out.
 */
const handleGontijoSessionUpdate = (event) => {
  if (!selectLoginOnCheckoutEnabled(store.getState())) return;

  const { user, error } = event ?? {};

  const token = typeof user?.token === 'string' ? user.token.trim() : '';

  if (!token) {
    if (endsSession(error)) store.dispatch(signOutGontijoSession());
    return;
  }

  store.dispatch(establishGontijoSession(token, user.expires, user.email));
};

export default handleGontijoSessionUpdate;
